// Memory X-ray — the two-pass "see inside the fleet's memory" pipeline behind
// the Lessons-tab X-ray button.
//
// The Lessons tab shows that agent-learned lessons are SILOED, but it groups by
// exact slug — so it can't see that `feedback_no_emdashes` and
// `feedback_no_em_dashes`, or six differently-named Linear-workflow notes, are
// the SAME underlying lesson. Adoption-by-slug therefore badly undercounts the
// shared "team canon" that's really there.
//
// This module runs an in-browser agent (runSubAgent — the same primitive the
// chat and influence-trace use) in two passes:
//
//   1. clusterMemories — feed the LLM the catalog of memory NAMES (slug, kind,
//      spread) and have it group same-lesson-different-slug memories and judge
//      each cluster's generalizability (team-canon / project / personal).
//   2. mergeCluster — for one cluster, hydrate the member BODIES and have the
//      LLM synthesize a single canonical memory, preserving every specific.
//
// clusterCoverage (from memoryEndpointMatrix) answers "who should have this" —
// the endpoints that hold no member of the cluster. This is ANALYSIS ONLY: it
// previews the synthesized memory + suggested recipients; it does not write to
// any endpoint (the dashboard is read-only).
import { listOrgLessons, listMemoryBodies } from "./memories.js";
import { runSubAgent } from "./subAgent.js";

// ── pass 1: cluster by name ──────────────────────────────────────────

export const CLUSTER_SCHEMA = {
    type: "object",
    properties: {
        clusters: {
            type: "array",
            description: "Groups of memories expressing the SAME underlying lesson/convention, even under different slugs. ONLY groups with 2+ members. Most-shareable first.",
            items: {
                type: "object",
                properties: {
                    theme: { type: "string", description: "Short canonical name for the shared lesson, e.g. 'Run tests before pushing'." },
                    generalizability: { type: "string", description: "team-canon | project-specific | personal | unclear" },
                    member_slugs: { type: "array", items: { type: "string" }, description: "Slugs from the catalog in this group." },
                    rationale: { type: "string", description: "1-2 sentences: the shared thought / why they belong together." },
                },
                required: ["theme", "generalizability", "member_slugs"],
            },
        },
        notes: { type: "string", description: "One line: how much shared structure exists vs siloed/personal." },
    },
    required: ["clusters"],
};

const CLUSTER_SYSTEM = [
    "You analyze the MEMORY files that AI coding-agent harnesses (Claude Code, etc.) write about their work, across a fleet of developer endpoints in one org.",
    "Goal: find GENERALIZABLE lessons that should be shared fleet-wide but currently live in only one place — raw material for propagating knowledge back to every endpoint.",
    "You get a catalog: slug, kind, how many endpoints (eps) and projects (proj) hold it, and trace count (n).",
    "Cluster memories expressing the SAME underlying lesson EVEN UNDER DIFFERENT SLUGS (semantic grouping; slugs won't match exactly).",
    "Judge generalizability: team-canon (every endpoint should have it — test/PR/tooling/style conventions), project-specific, personal (one dev's preference/context), or unclear.",
    "Only emit clusters with 2+ members. Be conservative; don't force unrelated memories together.",
].join("\n");

// Run the clustering pass. Returns { clusters, notes, catalogSize }. Throws on
// agent failure so the caller can show an actionable error.
export async function clusterMemories({ apiScope = [], range, signal, model = null, onEvent = null } = {}) {
    const lessons = await listOrgLessons({ apiScope, range, limit: 2000 });
    if (!lessons.length) return { clusters: [], notes: "No memories in scope.", catalogSize: 0 };
    const catalog = lessons.map((l) => ({ slug: l.slug, kind: l.kind, eps: l.endpointCount, proj: l.projectCount, n: l.value }));
    const r = await runSubAgent({
        role: "primary",
        model,
        tools: [],
        signal,
        onEvent,
        outputSchema: CLUSTER_SCHEMA,
        systemPrompt: CLUSTER_SYSTEM,
        task: `Memory catalog (${catalog.length} files):\n${JSON.stringify(catalog)}`,
    });
    if (r.status === "aborted") throw new DOMException("aborted", "AbortError");
    if (r.status !== "done" || !r.result) throw new Error(r.error?.message || r.reason || "clustering failed");
    const clusters = (r.result.clusters || []).filter((c) => (c.member_slugs || []).length >= 2);
    return { clusters, notes: r.result.notes || "", catalogSize: catalog.length };
}

// ── coverage: who already holds (any member of) a cluster ─────────────

// memoryEndpointMatrix (slug→endpoints from the `memory` signal) now lives in
// memories.js alongside the other signal readers; clusterCoverage takes its
// output as a param.

// Endpoints that hold ANY member of the cluster (have) vs the rest of the
// memory-writing fleet (missing — the suggested recipients).
export function clusterCoverage(cluster, matrix) {
    if (!matrix) return null;
    const have = new Set();
    for (const slug of cluster.member_slugs || []) {
        const s = matrix.slugEndpoints.get(slug);
        if (s) for (const e of s) have.add(e);
    }
    const haveEndpoints = [...have];
    const missingEndpoints = [...matrix.allEndpoints].filter((e) => !have.has(e));
    return { haveEndpoints, missingEndpoints, total: matrix.allEndpoints.size };
}

// ── pass 2: merge a cluster's bodies ─────────────────────────────────

export const MERGE_SCHEMA = {
    type: "object",
    properties: {
        name: { type: "string", description: "kebab-case slug for the canonical merged memory" },
        description: { type: "string", description: "one-line frontmatter-style description" },
        kind: { type: "string", description: "feedback | reference | project | user" },
        body: { type: "string", description: "Merged memory content as markdown. Synthesize ALL distinct nuances; preserve specifics (exact commands, per-subproject differences, policies). Do NOT lose information or invent rules." },
        preserved_nuances: { type: "array", items: { type: "string" }, description: "The distinct points carried over from the sources." },
        conflicts: { type: "array", items: { type: "string" }, description: "Contradictions between sources a human should resolve; empty if none." },
    },
    required: ["name", "description", "body", "preserved_nuances"],
};

const MERGE_SYSTEM = [
    "You merge several AI-coding-agent harness MEMORY files that all express the same underlying lesson into ONE canonical memory, to be propagated fleet-wide so every endpoint carries the shared convention.",
    "Preserve every distinct, specific nuance — exact commands, per-subproject differences, explicit policies. Losing a specific is a failure.",
    "Do NOT invent rules not present in the sources. If two sources conflict, keep both and flag the conflict.",
].join("\n");

// Hydrate the most-recent body for each member slug, then synthesize. Returns
// { merged, sources: [{slug, chars}] }. Throws if no bodies hydrate or the
// agent fails.
export async function mergeCluster({ memberSlugs = [], apiScope = [], range, signal, model = null, onEvent = null } = {}) {
    const sources = [];
    for (const slug of memberSlugs) {
        // Newest full-content body (write/read) for this member, straight from the
        // `memory` signal — an `edit` carries only a hunk, not a usable source.
        const bodies = await listMemoryBodies({ apiScope, range, slug, limit: 20 });
        const full = bodies.find((b) => (b.op === "write" || b.op === "read") && b.body);
        if (full) sources.push({ slug, body: full.body });
    }
    if (!sources.length) throw new Error("could not read any member bodies");
    const r = await runSubAgent({
        role: "primary",
        model,
        tools: [],
        signal,
        onEvent,
        outputSchema: MERGE_SCHEMA,
        systemPrompt: MERGE_SYSTEM,
        task: `Merge these ${sources.length} memories (same underlying lesson):\n\n${sources.map((s, i) => `=== SOURCE ${i + 1}: ${s.slug} ===\n${s.body}`).join("\n\n")}`,
    });
    if (r.status === "aborted") throw new DOMException("aborted", "AbortError");
    if (r.status !== "done" || !r.result) throw new Error(r.error?.message || r.reason || "merge failed");
    return { merged: r.result, sources: sources.map((s) => ({ slug: s.slug, chars: s.body.length })) };
}
